/**
 * Days for the panel's date pickers (src/ui/DatePicker.vue), as "YYYY-MM-DD" text.
 *
 * A day is a calendar day, not a moment: the arithmetic runs on UTC dates so a
 * daylight-saving change never adds or loses a day. "Today" is the browser's own
 * date, which for the stores and the people who run them is Vancouver's.
 *
 * The presets are Google Ads' date-range menu (owner, 2026-10-23: "every calendar
 * should be like the one in Google Ads"), with Google's meanings: "Last 7 days",
 * "Last 14 days" and "Last 30 days" end yesterday; "This week" and "This month"
 * run up to today; "N days up to today / yesterday" are the two custom spans.
 */
import { locale, tr } from "@/i18n";

const DAY_MS = 86400000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_RE = /^(\d{4})-(\d{2})$/;

export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTH_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
export const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TR_DAY = new Intl.DateTimeFormat("tr-TR", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });
const TR_DAY_LONG = new Intl.DateTimeFormat("tr-TR", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" });
const TR_MONTH = new Intl.DateTimeFormat("tr-TR", { timeZone: "UTC", month: "long", year: "numeric" });
const TR_MONTH_SHORT = MONTH_SHORT.map((_, i) => new Intl.DateTimeFormat("tr-TR", { timeZone: "UTC", month: "short" }).format(Date.UTC(2026, i, 1)));
const TR_MONTH_LONG = MONTH_LONG.map((_, i) => new Intl.DateTimeFormat("tr-TR", { timeZone: "UTC", month: "long" }).format(Date.UTC(2026, i, 1)));

export function monthsShort() {
  return locale.value === "tr" ? TR_MONTH_SHORT : MONTH_SHORT;
}

const pad = (n) => String(n).padStart(2, "0");

/** A real calendar day written as YYYY-MM-DD (2026-02-30 is not one). */
export function isDay(s) {
  const m = DAY_RE.exec(typeof s === "string" ? s : "");
  if (!m) return false;
  const t = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  return dayFromTime(t) === s;
}

/** A month written as YYYY-MM. */
export function isMonth(s) {
  const m = MONTH_RE.exec(typeof s === "string" ? s : "");
  return !!m && +m[2] >= 1 && +m[2] <= 12;
}

function timeOf(day) {
  const [y, m, d] = day.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function dayFromTime(t) {
  const d = new Date(t);
  return d.getUTCFullYear() + "-" + pad(d.getUTCMonth() + 1) + "-" + pad(d.getUTCDate());
}

export function makeDay(y, m, d) {
  return dayFromTime(Date.UTC(y, m - 1, d));
}

/** The browser's date today. */
export function today() {
  const now = new Date();
  return now.getFullYear() + "-" + pad(now.getMonth() + 1) + "-" + pad(now.getDate());
}

export function addDays(day, n) {
  return dayFromTime(timeOf(day) + n * DAY_MS);
}

/** How many days from `from` to `to`, both counted (the same day is 1). */
export function spanDays(from, to) {
  return Math.round((timeOf(to) - timeOf(from)) / DAY_MS) + 1;
}

/** 0 for Sunday … 6 for Saturday. */
export function weekday(day) {
  return new Date(timeOf(day)).getUTCDay();
}

/** The first day of the week `day` is in, for weeks that start on `weekStart` (0 Sunday, 1 Monday). */
export function startOfWeek(day, weekStart = 0) {
  return addDays(day, -((weekday(day) - weekStart + 7) % 7));
}

export function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function startOfMonth(day) {
  return day.slice(0, 8) + "01";
}

export function endOfMonth(day) {
  const y = +day.slice(0, 4);
  const m = +day.slice(5, 7);
  return day.slice(0, 8) + pad(daysInMonth(y, m));
}

/** "YYYY-MM" of a day. */
export function monthOf(day) {
  return day.slice(0, 7);
}

/** The month `n` months after a "YYYY-MM" month. */
export function addMonths(month, n) {
  const y = +month.slice(0, 4);
  const m = +month.slice(5, 7) - 1 + n;
  const yy = y + Math.floor(m / 12);
  return yy + "-" + pad(((m % 12) + 12) % 12 + 1);
}

/** Months from `a` to `b` ("YYYY-MM"), 0 when they are the same month. */
export function monthsBetween(a, b) {
  return (+b.slice(0, 4) - +a.slice(0, 4)) * 12 + (+b.slice(5, 7) - +a.slice(5, 7));
}

export function minDay(a, b) {
  return a <= b ? a : b;
}

export function maxDay(a, b) {
  return a >= b ? a : b;
}

/** `day` moved inside [min, max]; either bound may be empty. */
export function clampDay(day, min, max) {
  if (min && day < min) return min;
  if (max && day > max) return max;
  return day;
}

/** "Aug 1, 2026"; in Turkish "1 Ağu 2026" (parseDay reads both). */
export function formatDay(day) {
  if (!isDay(day)) return "";
  if (locale.value === "tr") return TR_DAY.format(timeOf(day));
  return MONTH_SHORT[+day.slice(5, 7) - 1] + " " + +day.slice(8, 10) + ", " + day.slice(0, 4);
}

/** "Saturday, August 1, 2026", for screen readers. */
export function formatDayLong(day) {
  if (locale.value === "tr") return TR_DAY_LONG.format(timeOf(day));
  return WEEKDAY_LONG[weekday(day)] + ", " + MONTH_LONG[+day.slice(5, 7) - 1] + " " + +day.slice(8, 10) + ", " + day.slice(0, 4);
}

/** "August 2026" for a "YYYY-MM". */
export function formatMonth(month) {
  if (locale.value === "tr") return TR_MONTH.format(Date.UTC(+month.slice(0, 4), +month.slice(5, 7) - 1, 1));
  return MONTH_LONG[+month.slice(5, 7) - 1] + " " + month.slice(0, 4);
}

/**
 * A range the short way, as Google Ads writes it: "Aug 1, 2026" (one day), "Aug 1 – 31, 2026",
 * "Aug 31 – Sep 6, 2026", "Dec 28, 2025 – Jan 3, 2026".
 */
export function formatRange(from, to) {
  if (!isDay(from) || !isDay(to)) return "";
  if (locale.value === "tr") return TR_DAY.formatRange(timeOf(from), timeOf(to));
  if (from === to) return formatDay(from);
  const [fy, fm, fd] = [from.slice(0, 4), +from.slice(5, 7), +from.slice(8, 10)];
  const [ty, tm, td] = [to.slice(0, 4), +to.slice(5, 7), +to.slice(8, 10)];
  if (fy !== ty) return formatDay(from) + " – " + formatDay(to);
  if (fm !== tm) return MONTH_SHORT[fm - 1] + " " + fd + " – " + MONTH_SHORT[tm - 1] + " " + td + ", " + ty;
  return MONTH_SHORT[fm - 1] + " " + fd + " – " + td + ", " + ty;
}

/**
 * A typed date, read the ways people write one here: 2026-08-01, 8/1/2026 (month first, as
 * in Canada and the US), 8/1/26, "Aug 1, 2026", "August 1 2026", "1 Aug 2026", and "Aug 1"
 * (in `year`); month names in English or Turkish ("1 Ağu 2026", "1 Ağustos 2026").
 * Returns "" when it is not a real day.
 */
export function parseDay(text, year) {
  const s = String(text || "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  if (isDay(s)) return s;
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(s);
  if (m) {
    const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    return checked(y, +m[1], +m[2]);
  }
  const monthIndex = (word) => {
    const bare = word.replace(/\.$/, "");
    if (bare.length < 3) return -1;
    const english = MONTH_LONG.findIndex((name) => name.toLowerCase().startsWith(bare.toLowerCase()));
    if (english >= 0) return english;
    const turkish = bare.toLocaleLowerCase("tr-TR");
    return TR_MONTH_LONG.findIndex((name) => name.toLocaleLowerCase("tr-TR").startsWith(turkish));
  };
  m = /^(\p{L}+\.?) (\d{1,2})(?:,? (\d{4}))?$/u.exec(s);
  if (m && monthIndex(m[1]) >= 0) return checked(m[3] ? +m[3] : year, monthIndex(m[1]) + 1, +m[2]);
  m = /^(\d{1,2}) (\p{L}+\.?),? (\d{4})$/u.exec(s);
  if (m && monthIndex(m[2]) >= 0) return checked(+m[3], monthIndex(m[2]) + 1, +m[1]);
  return "";
}

function checked(y, m, d) {
  if (!y || m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) return "";
  return makeDay(y, m, d);
}

/**
 * Google Ads' menu. Each preset gives [from, to] for `today`; `weekStart` picks the
 * "(Sun – …)" or "(Mon – …)" week of the two week presets.
 */
export const PRESETS = [
  { id: "today", label: () => tr("Today"), range: (t) => [t, t] },
  { id: "yesterday", label: () => tr("Yesterday"), range: (t) => [addDays(t, -1), addDays(t, -1)] },
  {
    id: "thisWeek",
    weekly: true,
    label: (ws) => (ws === 1 ? tr("This week (Mon – Today)") : tr("This week (Sun – Today)")),
    range: (t, ws) => [startOfWeek(t, ws), t],
  },
  { id: "last7", label: () => tr("Last 7 days"), range: (t) => [addDays(t, -7), addDays(t, -1)] },
  {
    id: "lastWeek",
    weekly: true,
    label: (ws) => (ws === 1 ? tr("Last week (Mon – Sun)") : tr("Last week (Sun – Sat)")),
    range: (t, ws) => [addDays(startOfWeek(t, ws), -7), addDays(startOfWeek(t, ws), -1)],
  },
  { id: "last14", label: () => tr("Last 14 days"), range: (t) => [addDays(t, -14), addDays(t, -1)] },
  { id: "thisMonth", label: () => tr("This month"), range: (t) => [startOfMonth(t), t] },
  { id: "last30", label: () => tr("Last 30 days"), range: (t) => [addDays(t, -30), addDays(t, -1)] },
  {
    id: "lastMonth",
    label: () => tr("Last month"),
    range: (t) => {
      const end = addDays(startOfMonth(t), -1);
      return [startOfMonth(end), end];
    },
  },
];

/** [from, to] of "N days up to today" (upTo "today") or "N days up to yesterday". */
export function daysUpTo(n, upTo, t = today()) {
  const end = upTo === "yesterday" ? addDays(t, -1) : t;
  return [addDays(end, -(n - 1)), end];
}
